<template>
    <v-card-title>{{ $t("sidebar.companyCheck") }} </v-card-title>

    <v-card-text>
        <div class="d-flex flex-row justify-content-center mb-5">
            <v-text-field
                v-model="searchQuery"
                :label="$t('common.search')"
                prepend-inner-icon="mdi-magnify"
                @keydown.enter="onSearch"
                hide-details
                clearable
            />
            <v-btn
                color="primary"
                class="ml-2"
                :loading="loading"
                @click="onSearch"
            >
                {{ $t("common.search") }}
            </v-btn>
        </div>
        <v-data-table-server
            :headers="translatedHeaders"
            :items="companies"
            :items-length="totalCount"
            :loading="loading"
            class="elevation-1"
            item-value="id"
            density="compact"
            :no-data-text="$t('company.noCompaniesFound')"
            v-model:page="page"
            v-model:items-per-page="pageSize"
            :items-per-page-options="[10, 25, 50, 100]"
            @update:options="fetchCompanies"
            @click:row="goToCompanyDetails"
            hover
            clickable
        />
    </v-card-text>
</template>

<script>
import axios from "axios";
import { showAlertModal, formatDisplayDate } from "@/utils";

export default {
    name: "CompanySearch",
    data() {
        return {
            searchQuery: "",
            companies: [],
            page: 1,
            pageSize: 10,
            totalCount: 0,
            loading: false,
            abortCtrl: null,
        };
    },
    computed: {
        translatedHeaders() {
            return [
                { title: this.$t("company.name"), key: "name" },
                {
                    title: this.$t("register.companyType.title"),
                    key: "type_display",
                },
                { title: this.$t("company.dateRegistered"), key: "created_at" },
            ];
        },
    },
    mounted() {
        this.fetchCompanies();
    },
    beforeUnmount() {
        /* Cancels any in-flight request */
        if (this.abortCtrl) this.abortCtrl.abort();
    },
    methods: {
        showAlertModal,
        goToCompanyDetails(event, company) {
            if (company?.item?.id) {
                this.$router.push({
                    name: "CompanyDetails",
                    params: { id: company.item.id },
                });
            }
        },
        onSearch() {
            this.page = 1;
            this.fetchCompanies();
        },
        formatDate(dateStr) {
            return formatDisplayDate(dateStr);
        },
        getTypeDisplay(type) {
            const typeMap = {
                0: this.$t("register.companyType.transportator"),
                1: this.$t("register.companyType.speditie"),
                2: this.$t("register.companyType.producator"),
            };
            return typeMap[type] || this.$t("company.unknownType");
        },
        async fetchCompanies(options) {
            // When the table emits update:options we get { page, itemsPerPage, ... }. Sync only when present
            // (e.g. from mounted/onSearch we call fetchCompanies() with no args, so options is undefined).
            if (options && typeof options === "object") {
                this.page = options.page ?? this.page;
                this.pageSize = options.itemsPerPage ?? this.pageSize;
            }

            // cancel any previous call
            this.abortCtrl?.abort();
            const controller = new AbortController();
            this.abortCtrl = controller;

            this.loading = true;
            try {
                const response = await axios.get(`${process.env.VUE_APP_API_URL}/companies/`, {
                    params: {
                        search: this.searchQuery || undefined,
                        page: this.page,
                        page_size: this.pageSize,
                    },
                    signal: controller.signal,
                });

                // GET /companies/ returns a paginated object: count (total), results (current page).
                const data = response?.data;
                const count = data?.count;
                const results = data?.results;
                if (typeof count !== "number" || !Array.isArray(results)) {
                    this.totalCount = 0;
                    this.companies = [];
                    return;
                }

                this.totalCount = count;
                this.companies = results.map((c) => ({
                    ...c,
                    type_display: this.getTypeDisplay(c.type),
                    created_at: this.formatDate(c.created_at),
                }));
            } catch (err) {
                const isCanceled =
                    axios.isCancel?.(err) ||
                    err?.code === "ERR_CANCELED" ||
                    err?.name === "CanceledError";

                if (!isCanceled) {
                    console.error("Error fetching companies:", err);
                    this.showAlertModal(
                        this.$store,
                        this.$t("company.fetchError"),
                        "danger",
                        12_000,
                        err
                    );
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
